export const INTAKE_LEAD_GENERATION_KEY = 'intake_lead_generation' as const;
export const INTAKE_LEAD_GENERATION_MODELS = ['gpt-4o-mini', 'gpt-4.1-mini'] as const;

export interface IntakeLeadGenerationSettings {
  /** Provider model used for bounded lead drafting. */
  model: string;
  /** Per-tenant UTC-day circuit breaker; disabling is handled by the Switch state. */
  dailyBudgetUsd: number;
  /** Hard upper bound for one bulk request. */
  maxBatchSize: number;
  /** Maximum source characters sent to the model for one intake record. */
  maxSourceCharacters: number;
}

export const DEFAULT_INTAKE_LEAD_GENERATION_SETTINGS: IntakeLeadGenerationSettings = {
  model: 'gpt-4o-mini',
  dailyBudgetUsd: 5,
  maxBatchSize: 25,
  maxSourceCharacters: 6_000,
};

export function normalizeIntakeLeadGenerationSettings(
  value: unknown,
): IntakeLeadGenerationSettings {
  const input = isRecord(value) ? value : {};
  return {
    model: typeof input.model === 'string'
      && INTAKE_LEAD_GENERATION_MODELS.includes(input.model.trim() as typeof INTAKE_LEAD_GENERATION_MODELS[number])
      ? input.model.trim()
      : DEFAULT_INTAKE_LEAD_GENERATION_SETTINGS.model,
    dailyBudgetUsd: boundedNumber(
      input.dailyBudgetUsd,
      DEFAULT_INTAKE_LEAD_GENERATION_SETTINGS.dailyBudgetUsd,
      0.01,
      1_000,
    ),
    maxBatchSize: Math.round(boundedNumber(
      input.maxBatchSize,
      DEFAULT_INTAKE_LEAD_GENERATION_SETTINGS.maxBatchSize,
      1,
      50,
    )),
    maxSourceCharacters: Math.round(boundedNumber(
      input.maxSourceCharacters,
      DEFAULT_INTAKE_LEAD_GENERATION_SETTINGS.maxSourceCharacters,
      500,
      20_000,
    )),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function boundedNumber(value: unknown, fallback: number, min: number, max: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(Math.max(parsed, min), max);
}
